export type ImageGenerationState =
  'queued' | 'submitted' | 'running' | 'succeeded' | 'failed';

export interface ImageGenerationRequestResource {
  requestId: string;
  state: ImageGenerationState;
  statusUrl: string;
  failureCode?: string;
  picture?: unknown;
}

export interface ImageGenerationEnvelope {
  picture?: unknown;
  imageGenerationRequest?: ImageGenerationRequestResource;
}

export type ImageGenerationPlacement =
  'full_page_background' | 'card_image' | 'inline_image';

export interface ImageGenerationSlot {
  placement: ImageGenerationPlacement;
  itemId?: number | string;
  slotKey?: string;
}

export interface ImageGenerationContext {
  templateName: string;
  placement: ImageGenerationPlacement;
  sequenceId: string;
  itemId?: string;
  slotKey?: string;
}

type RendererSequenceIdentity = {
  id?: number | string;
  templateName?: string;
};

const TEMPLATE_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,127}$/;
const SEQUENCE_ID_PATTERN = /^[1-9][0-9]{0,18}$/;
const ITEM_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9:_-]{0,127}$/;
const SLOT_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const REQUEST_ID_PATTERN = /^image_request_[a-f0-9]{32}$/;
const MAX_SEQUENCE_ID = BigInt('9223372036854775807');

export function buildImageGenerationContext(
  sequence: RendererSequenceIdentity | null | undefined,
  slot: ImageGenerationSlot
): ImageGenerationContext | undefined {
  const templateName = sequence?.templateName?.trim();
  const sequenceId = String(sequence?.id ?? '');
  if (
    !templateName ||
    !TEMPLATE_NAME_PATTERN.test(templateName) ||
    !SEQUENCE_ID_PATTERN.test(sequenceId) ||
    BigInt(sequenceId) > MAX_SEQUENCE_ID
  ) {
    return undefined;
  }

  const itemId = slot.itemId == null ? undefined : String(slot.itemId);
  const slotKey = slot.slotKey?.trim();
  return {
    templateName,
    placement: slot.placement,
    sequenceId,
    ...(itemId && ITEM_ID_PATTERN.test(itemId) ? { itemId } : {}),
    ...(slotKey && SLOT_KEY_PATTERN.test(slotKey) ? { slotKey } : {})
  };
}

export class ImageGenerationPollingError extends Error {
  constructor(public readonly code: string) {
    super(code);
  }
}

export function imageGenerationFailureMessage(code: string): string {
  switch (code) {
    case 'content_policy_rejected':
      return 'This image request did not pass the AI safety checks. Try rephrasing your description or choosing a different scene.';
    case 'provider_request_rejected':
      return 'This image request could not be processed. Please try again, or contact support if the problem continues.';
    case 'request_expired':
    case 'generation_timed_out':
      return 'Image generation took too long. Please try again.';
    case 'platform_unavailable':
      return 'Image generation is temporarily unavailable. Please try again shortly.';
    default:
      return 'Image generation could not be completed. Please try again.';
  }
}

export function appendImageGenerationContext(
  formData: FormData,
  context: ImageGenerationContext
): void {
  formData.append('context[template_name]', context.templateName);
  formData.append('context[placement]', context.placement);
  formData.append('context[sequence_id]', context.sequenceId);
  if (context.itemId) formData.append('context[item_id]', context.itemId);
  if (context.slotKey) formData.append('context[slot_key]', context.slotKey);
}

export function resolveImageGenerationStatusUrl(
  statusUrl: string,
  backendOrigin: string | undefined,
  companyId: number | string
): string {
  if (!backendOrigin) {
    throw new ImageGenerationPollingError('invalid_backend_origin');
  }

  let url: URL;
  let expectedOrigin: string;
  try {
    url = new URL(statusUrl, backendOrigin);
    expectedOrigin = new URL(backendOrigin).origin;
  } catch {
    throw new ImageGenerationPollingError('invalid_status_url');
  }

  const expectedPrefix = `/api/companies/${encodeURIComponent(
    String(companyId)
  )}/course_editor/pictures/generation_requests/`;
  const requestId = url.pathname.slice(expectedPrefix.length);
  if (
    url.origin !== expectedOrigin ||
    !url.pathname.startsWith(expectedPrefix) ||
    !REQUEST_ID_PATTERN.test(requestId) ||
    url.search ||
    url.hash
  ) {
    throw new ImageGenerationPollingError('invalid_status_url');
  }

  return url.toString();
}

type PollOptions = {
  fetchStatus: (statusUrl: string) => Promise<ImageGenerationEnvelope>;
  wait?: (signal?: AbortSignal) => Promise<void>;
  maxAttempts?: number;
  signal?: AbortSignal;
};

const defaultWait = (signal?: AbortSignal) =>
  new Promise<void>(resolve => {
    const finish = () => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', finish);
      resolve();
    };
    const timer = window.setTimeout(finish, 3000);
    signal?.addEventListener('abort', finish, { once: true });
    if (signal?.aborted) finish();
  });

export async function waitForGeneratedImage(
  initial: ImageGenerationEnvelope,
  { fetchStatus, wait = defaultWait, maxAttempts = 400, signal }: PollOptions
): Promise<ImageGenerationEnvelope> {
  if (signal?.aborted) throw new ImageGenerationPollingError('aborted');
  if (initial.picture) return initial;

  let resource = initial.imageGenerationRequest;
  if (!resource?.requestId || !resource.statusUrl) {
    throw new ImageGenerationPollingError('invalid_response');
  }

  for (let attempt = 0; attempt <= maxAttempts; attempt += 1) {
    if (signal?.aborted) throw new ImageGenerationPollingError('aborted');
    if (resource.state === 'failed') {
      throw new ImageGenerationPollingError(
        resource.failureCode || 'generation_failed'
      );
    }
    if (resource.state === 'succeeded') {
      if (!resource.picture) {
        throw new ImageGenerationPollingError('invalid_response');
      }
      return { picture: resource.picture };
    }
    if (attempt === maxAttempts) break;

    await wait(signal);
    if (signal?.aborted) throw new ImageGenerationPollingError('aborted');
    const next = await fetchStatus(resource.statusUrl);
    resource = next.imageGenerationRequest;
    if (!resource?.requestId || !resource.statusUrl) {
      throw new ImageGenerationPollingError('invalid_response');
    }
  }

  throw new ImageGenerationPollingError('generation_timed_out');
}
